/**
 * Standard product page behaviour.
 *
 * Resolves the selected option pills to a variant and updates price, image,
 * availability and the hidden id the form posts. Without JavaScript the form
 * still submits the server-rendered variant, so the page remains functional.
 */
(function () {
  'use strict';

  class ProductPage extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialised) return;
      this.dataset.initialised = 'true';

      const data = this.querySelector('[data-product-variants]');
      if (!data) return;

      try {
        this.variants = JSON.parse(data.textContent);
      } catch (error) {
        console.warn('Product: could not parse variant data.', error);
        return;
      }

      this.variantInput = this.querySelector('[data-variant-input]');
      this.image = this.querySelector('[data-product-image]');
      this.priceEl = this.querySelector('[data-product-price]');
      this.compareEl = this.querySelector('[data-product-compare]');
      this.stockEl = this.querySelector('[data-product-stock]');
      this.submit = this.querySelector('[data-product-submit]');

      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-option-index]')) this.update();
      });

      this.querySelectorAll('[data-thumb]').forEach((btn) =>
        btn.addEventListener('click', () => this.showImage(btn))
      );

      this.update();
    }

    get selected() {
      // One checked radio per option group, ordered by the option's index, so
      // the result lines up with the variant's own `options` array.
      const chosen = [];
      this.querySelectorAll('[data-option-index]:checked').forEach((input) => {
        chosen[Number(input.dataset.optionIndex)] = input.value;
      });
      return chosen;
    }

    update() {
      const chosen = this.selected;
      const variant = chosen.length
        ? this.variants.find((v) => v.options.every((o, i) => o === chosen[i]))
        : this.variants[0];

      // Cross out combinations that do not exist or are sold out, so the dead
      // end is visible before the visitor commits to it.
      this.querySelectorAll('[data-option-index]').forEach((input) => {
        const index = Number(input.dataset.optionIndex);
        const probe = chosen.slice();
        probe[index] = input.value;
        const match = this.variants.find((v) =>
          v.options.every((o, i) => (i === index ? o === input.value : o === probe[i]))
        );
        input.disabled = !match || !match.available;
      });

      if (!variant) {
        this.setUnavailable();
        return;
      }

      this.variantInput.value = variant.id;
      if (this.priceEl) this.priceEl.textContent = variant.price;

      if (this.compareEl) {
        this.compareEl.hidden = !variant.compareAt;
        if (variant.compareAt) this.compareEl.textContent = variant.compareAt;
      }

      if (variant.image && this.image && this.image.getAttribute('src') !== variant.image) {
        this.image.src = variant.image;
      }

      if (this.submit) {
        this.submit.disabled = !variant.available;
        this.submit.textContent = variant.available
          ? this.submit.dataset.labelAdd || this.submit.textContent
          : 'Sold out';
      }

      if (this.stockEl) {
        this.stockEl.textContent = variant.available ? '' : 'Out of stock';
        this.stockEl.dataset.level = variant.available ? 'ok' : 'out';
      }

      // Keep the address bar in step with the chosen variant so the page can be
      // shared or reloaded on the same selection.
      if (this.dataset.url && window.history?.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
    }

    setUnavailable() {
      if (this.submit) {
        this.submit.disabled = true;
        this.submit.textContent = 'Unavailable';
      }
      if (this.stockEl) {
        this.stockEl.textContent = 'This combination is not available';
        this.stockEl.dataset.level = 'out';
      }
    }

    showImage(button) {
      if (this.image) this.image.src = button.dataset.thumb;
      this.querySelectorAll('[data-thumb]').forEach((b) =>
        b.setAttribute('aria-current', String(b === button))
      );
    }
  }

  if (!customElements.get('product-page')) {
    customElements.define('product-page', ProductPage);
  }
})();
